// utils/calculateExperience.js

/**
 * Calculates total work experience in decimal years from an array of experience entries.
 * Handles overlapping jobs by summing months as-is (industry standard).
 *
 * @param {Array} experiences - Array of experience objects with startDate and endDate
 * @returns {number|string} - Total experience as decimal years (e.g. 5.5) or "" if no valid entries
 */
export function calculateTotalExperience(experiences = []) {
  if (!experiences?.length) return "";

  let totalMonths = 0;

  for (const exp of experiences) {
    const start = parseDate(exp?.startDate);
    const end = parseDate(exp?.endDate) ?? new Date();

    if (!isValidDateRange(start, end)) continue;

    const today = new Date();
    const effectiveEnd = end > today ? today : end;

    let months =
      (effectiveEnd.getFullYear() - start.getFullYear()) * 12 +
      (effectiveEnd.getMonth() - start.getMonth());

    months += 1;

    if (!shouldCountEndMonth(effectiveEnd)) {
      months -= 1;
    }

    if (months > 0) totalMonths += months;
  }

  if (totalMonths === 0) return "";

  const years = Math.floor(totalMonths / 12);
  const remainMonths = totalMonths % 12;

  if (remainMonths === 0) return String(years);
  if (years === 0) return `0.${remainMonths}`;
  return `${years}.${remainMonths}`;
}

function shouldCountEndMonth(date) {
  const day = date.getDate();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return day >= lastDay - 1;
}


// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parses a date string into a Date object.
 * Handles a wide range of formats produced by resume parsers:
 *   "MM-YYYY", "YYYY-MM", "MM/YYYY", "MM.YYYY"
 *   "Jan 2020", "January 2020"
 *   "2020"  (year only → assumes January)
 *   "present", "current", "till date", "now", "ongoing", "–"
 *
 * @param {string} value
 * @returns {Date|null}
 */
function parseDate(value) {
  if (!value) return null;

  const v = String(value).trim().toLowerCase();

  // ── Present / ongoing markers ──
  const presentMarkers = ["present", "current", "till date", "now", "ongoing", "–", "-"];
  if (presentMarkers.includes(v)) return new Date();

  // ── "Jan 2020" or "January 2020" ──
  const monthNameMatch = v.match(/^([a-z]+)\s+(\d{4})$/);
  if (monthNameMatch) {
    const month = parseMonthName(monthNameMatch[1]);
    const year = parseInt(monthNameMatch[2], 10);
    if (month !== -1 && !isNaN(year)) return new Date(year, month, 1);
  }

  // ── Year only: "2020" ──
  if (/^\d{4}$/.test(v)) {
    const year = parseInt(v, 10);
    return new Date(year, 0, 1); // assume January
  }

  // ── Normalize separators (/ and . → -) then split ──
  const normalized = v.replace(/[\/\.]/g, "-");
  const parts = normalized.split("-");

  if (parts.length !== 2) return null;

  let month, year;

  if (parts[0].length === 4) {
    // YYYY-MM
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10) - 1;
  } else {
    // MM-YYYY
    month = parseInt(parts[0], 10) - 1;
    year = parseInt(parts[1], 10);
  }

  if (isNaN(month) || isNaN(year)) return null;

  return new Date(year, month, 1);
}

/**
 * Converts a month name (or prefix) to a 0-based month index.
 * e.g. "jan" → 0, "december" → 11, "unknown" → -1
 *
 * @param {string} str
 * @returns {number} 0–11 or -1 if not matched
 */
function parseMonthName(str) {
  const months = ["jan", "feb", "mar", "apr", "may", "jun",
    "jul", "aug", "sep", "oct", "nov", "dec"];
  return months.findIndex((m) => str.startsWith(m));
}

/**
 * Validates that a date range is plausible.
 * Rejects: missing start, start after end, unrealistic years, invalid months.
 *
 * @param {Date|null} start
 * @param {Date|null} end
 * @returns {boolean}
 */
function isValidDateRange(start, end) {
  if (!start) return false;

  // Invalid month (e.g. month 13 or month -1 from "00-2020")
  const startMonth = start.getMonth();
  if (startMonth < 0 || startMonth > 11) return false;

  // Unrealistic years
  const currentYear = new Date().getFullYear();
  if (start.getFullYear() < 1950) return false;
  if (start.getFullYear() > currentYear) return false; // future start date

  // Start must be before end (if end exists)
  if (end && start > end) return false;

  return true;
}